import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Scene from './Scene';
import gameLogic from './Scene/gameLogic';
import BuddhaGameUI from './ui';
import {
  DEFAULT_LEVEL_ID,
  LEVEL_CONFIGS,
  getLevelConfig,
} from './levelConfigs';
import { useAuth } from '@lib/useAuth';
import { useUserRole } from '@lib/useUserRole';
import {
  fetchUserBadgesWithDetails,
  has14DayStreakBadge,
  type UserBadgeWithDetails,
} from '@lib/badges';

const TOTAL_LEVELS = LEVEL_CONFIGS.length;

function BackgroundLayer({ videoUrl }: { videoUrl?: string }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-black"
    >
      {videoUrl ? (
        <video
          key={videoUrl}
          className="h-full w-full object-cover"
          src={videoUrl}
          autoPlay
          muted
          loop
          playsInline
          poster="/buddha-game/background.jpg"
          preload="auto"
          crossOrigin="anonymous"
        />
      ) : (
        <img
          src="/buddha-game/background.jpg"
          alt="Calm lake backdrop"
          className="h-full w-full object-cover"
          loading="lazy"
        />
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/45" />
    </div>
  );
}

// Debug overlay removed

// Main entry for the Buddha Game page. Sets up an empty full-screen container
// where the Three.js scene will be mounted in future steps.
export default function BuddhaGame() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { role } = useUserRole(user?.id);

  const [userBadges, setUserBadges] = useState<UserBadgeWithDetails[]>([]);
  const [badgesLoading, setBadgesLoading] = useState(false);

  const refreshUserBadges = useCallback(async () => {
    if (!user?.id) {
      setUserBadges([]);
      return;
    }
    setBadgesLoading(true);
    try {
      const badges = await fetchUserBadgesWithDetails(user.id);
      setUserBadges(badges);
    } catch (e) {
      console.debug('BuddhaGame: failed to refresh user badges', e);
      setUserBadges([]);
    } finally {
      setBadgesLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void refreshUserBadges();
  }, [refreshUserBadges]);

  const has14DayBadge = useMemo(
    () => has14DayStreakBadge(userBadges),
    [userBadges]
  );
  const levelParam = Number.parseInt(
    searchParams.get('level') ?? String(DEFAULT_LEVEL_ID),
    10
  );
  const requestedLevelId = Number.isFinite(levelParam)
    ? Math.min(Math.max(levelParam, DEFAULT_LEVEL_ID), TOTAL_LEVELS)
    : DEFAULT_LEVEL_ID;

  // Gate Level 2+ behind the 14-Day Streak Badge.
  // Default to Level 1 until badge state is known/loaded.
  const effectiveLevelId =
    requestedLevelId >= 2 && !has14DayBadge
      ? DEFAULT_LEVEL_ID
      : requestedLevelId;

  const levelConfig = useMemo(
    () => getLevelConfig(effectiveLevelId),
    [effectiveLevelId]
  );

  const adminTotalCyclesOverride = useMemo(() => {
    if (role !== 'admin') return null;

    const raw = searchParams.get('totalCycles');
    if (!raw) return null;

    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;

    // Guard against absurd values that could lead to near-zero progress.
    // Keep this minimal: just avoid accidental 0/negative and huge numbers.
    return Math.min(parsed, 1000);
  }, [role, searchParams]);

  const goalOverrides = useMemo(() => {
    const baseOverrides = levelConfig.goalOverrides ?? undefined;
    if (!adminTotalCyclesOverride) return baseOverrides;

    // Translate a desired total cycle count into per-cycle progress.
    // We also disable streak bonuses so the cycle count is predictable.
    const fallbackGoalHeight = 5.5;
    const goalHeight = baseOverrides?.goalHeight ?? fallbackGoalHeight;

    return {
      ...(baseOverrides ?? {}),
      progressPerCycle: goalHeight / adminTotalCyclesOverride,
      streakBonusPerSuccess: 0,
      maxStreakBonus: 0,
    };
  }, [adminTotalCyclesOverride, levelConfig.goalOverrides]);

  // Debug toggle removed
  useEffect(() => {
    gameLogic.configureLevel({
      breathingPattern: levelConfig.breathingPattern,
      penaltyMultiplier: levelConfig.penaltyMultiplier,
      voiceAudioEnabled: levelConfig.voiceAudioEnabled,
      strictHoldTiming: levelConfig.strictHoldTiming,
      inputWindowRatioMultiplier: levelConfig.inputWindowRatioMultiplier,
      goalOverrides,
    });

    const container = document.getElementById('buddha-scene-root');
    if (!container) return;

    // Recreate the scene whenever the active level changes so assets refresh.
    Scene.disposeScene();
    Scene.initScene(container, {
      backgroundVideoUrl: levelConfig.backgroundVideoUrl,
    });

    // initialize the Three.js scene and render loop (no visuals yet)
    return () => {
      Scene.disposeScene();
    };
  }, [levelConfig, goalOverrides]);

  // Prevent horizontal scrolling while the game page is mounted (mobile)
  useEffect(() => {
    document.body.classList.add('overflow-x-hidden');
    return () => {
      document.body.classList.remove('overflow-x-hidden');
    };
  }, []);

  // Debug toggle removed

  // Debug status polling removed

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-black/0">
      <BackgroundLayer videoUrl={levelConfig.backgroundVideoUrl} />

      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-10"
      >
        <div id="buddha-progress-layer" className="relative h-full w-full" />
      </div>

      <div
        id="buddha-scene-root"
        aria-label="Buddha Game 3D scene container"
        className="fixed inset-0 z-20 flex items-center justify-center"
      />

      {/* Breathing UI overlay */}
      <BuddhaGameUI
        levelIndex={levelConfig.id}
        totalLevels={TOTAL_LEVELS}
        has14DayStreakBadge={has14DayBadge}
        refreshUserBadges={refreshUserBadges}
        userBadgesLoading={badgesLoading}
      />

      {/* Debug overlay removed */}
    </div>
  );
}
